import { html, render, TemplateResult } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

export interface PageResult<RowClass> {
    count: number;
    pageCount: number;
    pageSize: number;
    pageIndex: number;
    //Note. remove the Null as soon as null is removed from the generated code so that they are compatible
    results: RowClass[] | null;
}

export interface RequestPage {
    pageIndex: number;
    pageSize: number;
    sortField: string;
    sortAsc: boolean;
}

export type HTMLElementProvider = () => HTMLElement;

/*this lets us easily bypass the typescript jquery definitions which are not what we want to use for datatables as the
typescript is out of date.
DataTables must be added to the main page directly, not compiled in*/
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const $j = $.fn as any;

//this class will build everything into its own div, which may then be embedded into a container.
export class TableView<RowClass> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataTable: any | null = null;
    firstRender = false;
    ui: HTMLDivElement;
    lastRefresh: Date = new Date();
    constructor() {
        this.ui = document.createElement("DIV") as HTMLDivElement;
    }

    htmlEncode(value: string): string {
        return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    //probably only needs to be called a single time, as after that the datatable is self manipulating.
    public render() {
        render(this.template(), this.ui);
        if (!this.firstRender) {
            this.firstRender = true;
            this.firstRendered();
        }
    }

    //this may be overwritten and added to, as long as the base call is always called.
    firstRendered() {
        this.connectDataTable();
    }

    //build and create the datatable.
    connectDataTable() {
        const dataTableElement = this.ui.querySelector('.data-table');
        if (dataTableElement) {
            const $dataTable = $(dataTableElement) as any;
            if (!$j.dataTable.isDataTable($dataTable)) {
                //lets build the datatable
                const columns = this.getColumns();
                const sortFieldIndex: number = this.getDefaultSortFieldIndex(columns);
                const sortAsc: string = this.getDefaultSortAsc() ? "asc" : "desc";

                this.dataTable = $dataTable.DataTable({
                    scrollX: true,
                    scrollY: this.verticalHeight(),
                    scrollCollapse: true,
                    paging: this.usePaging(),
                    order: [[sortFieldIndex, sortAsc]],
                    pageLength: this.pageLength(),
                    lengthChange: false,
                    processing: true,
                    serverSide: true,
                    ajax: (request: any, drawCallback, settings) => this.getPageData(request, drawCallback, settings),
                    //data: dataset,
                    searching: false,
                    info: true,
                    columns: columns,
                    //fixedColumns: { left: 1 },
                    autoWidth: this.useAutoWidthColumns()
                });
                this.dataTable.columns.adjust();
                this.bindClickEvents($dataTable);
                //bind in click events an other things here.

            } else {
                this.dataTable = $dataTable.DataTable();
                this.dataTable.rows().invalidate();
            }

        } else throw new Error("could not bind 'data-table'");
    }

    /*this must be overridden with the specific api calls to the server to get the data,
    and transform it to the appropriate result type required.*/
    async getRowsFromServer(request: RequestPage): Promise<PageResult<RowClass>> {
        return {
            count: 0,
            pageCount: 0,
            pageIndex: request.pageIndex,
            pageSize: request.pageSize,
            results: []
        };
    }

    getDefaultSortAsc(): boolean {
        //override as needed
        return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getDefaultSortFieldIndex(_columns: any[]): number {
        //override is required
        return 0;
    }

    pageLength(): number {
        //override as needed
        return 200;
    }

    usePaging(): boolean {
        //override as needed
        return true;
    }

    enableFiltering(): boolean {
        //override as needed
        return false;
    }

    updateFilter(_searchTerm: string | null) {
        //Override as needed
        return;
    }

    /*override as needed. this sets the vertical height of the grid, and must be a fixed size. the grid will scroll internally
    to deal with extra data*/
    verticalHeight(): string {
        return '60vh';
    }

    /*override as needed. if this is changed to false
    then the grid template needs to set a width that exactly matches the width of all columns,
    otherwise horizontal scrolling does not work correctly*/
    useAutoWidthColumns(): boolean {
        return false;
    }

    //must override and provide a correct set of columns, which will render based on the RowType class fields
    getColumns(): any[] {
        /* // datatable column definitions
                {

                    title: '<i class="fas fa-star"></i>',
                    width: "40px",
                    data: "windowId",
                    render: (value) => {
                        const favClassVal = (this.isFavourite(value) ? "fas " : "far ") + " fa-star text-warning favourite ";
                        const jobClassVal = (this.isJob(value) ? "text-success " : "text-secondary ") + " fas fa-home  job";
                        const val = `<i class="${favClassVal}" ></i> <i class="${jobClassVal}"  ></i>`;
                        return val;
                    }
                },
        */
        return [];
    }

    public template(): TemplateResult {
        const autoSize = this.useAutoWidthColumns();
        let style = ``;

        //if we are not autosizing, and there is a chance that we have overflow, or other issues
        //we need to set the width of the table to be exactly the width of all columns
        if (!autoSize) {
            let width = 0;
            const getWidth = (w: string) => {
                const num = parseInt(w.slice(0, w.length - 2));
                return isNaN(num) ? 100 : num;
            };
            this.getColumns().forEach(value => width += getWidth(value.width));
            style = `style="width:${width}px"`;
        }

        const unsafe = `<table class="data-table table cell-border table-striped" ${style}>`;

        return html`
            ${unsafeHTML(unsafe)}
            <thead>
            </thead>
            <tbody></tbody>
            </table>
        `;
    }

    /*this routine should be called anytime something external to the base class has changed which would impact on the content
    of a page of data as supplied by the server.
    examples of this are external filters etc which might change and need to trigger a reload
    this always resets the page back to the first page, as the current page may no longer exist based on the changes.*/
    public async refreshData(sinceLastRefreshMinutes?: number): Promise<void> {
        return await new Promise<void>((resolve) => {
            this.dataTable.ajax.reload(resolve, true);
        });
    }

    public bindOrRefreshContainer(containerProvider: HTMLElementProvider) {
        const container = containerProvider();
        if (this.ui.parentElement !== null && this.ui.parentElement !== container)
            this.ui.remove();
        if (container) {
            this.render();
            container.append(this.ui);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected bindClickEvents(_$dataTable: any) {
        //override;

    }

    /*do not touch or override this. this is a wrapper between our application api and the datatables interface
          This can be enhanced to utilize page caching if we desire.*/
    private getPageData(request: any, drawCallback: any, settings: any) {
        const sortFieldIndex: number = this.getDefaultSortFieldIndex(settings.aoColumns);
        let sortField: string = settings.aoColumns[sortFieldIndex].data;
        let sortAsc: boolean = this.getDefaultSortAsc();
        if (request.order?.length > 0) {
            sortField = settings.aoColumns[request.order[0].column ?? 0].data ?? sortField;
            sortAsc = request.order[0].dir === "asc";
        }
        const pageIndex = request.start / request.length;
        this.getRowsFromServer({
            pageIndex: pageIndex,
            pageSize: this.pageLength(),
            sortField: sortField,
            sortAsc: sortAsc
        })
            .then(results => {
                this.lastRefresh = new Date();
                drawCallback({
                    draw: request.draw,
                    recordsTotal: results.count,
                    recordsFiltered: results.count,
                    data: results.results
                });
            });

    }

}